import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Send, Bot, User } from 'lucide-react';
import { PageTitle } from '../components/PageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Invest = () => {
  const { user } = useAuth();
  const { theme, themes } = useTheme();
  const currentTheme = themes[theme] || themes.electric;
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Crear o recuperar session_id
    let sid = localStorage.getItem('invest_session_id');
    if (!sid) {
      sid = `invest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('invest_session_id', sid);
    }
    setSessionId(sid);
    
    // Cargar historial guardado
    const savedMessages = localStorage.getItem(`invest_messages_${sid}`);
    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages));
      } catch (e) {
        console.error('Error loading messages:', e);
      }
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    
    // Guardar mensajes en localStorage
    if (sessionId && messages.length > 0) {
      localStorage.setItem(`invest_messages_${sessionId}`, JSON.stringify(messages));
    }
  }, [messages, sessionId]);

  const sendMessage = async () => {
    if (!message.trim() || loading) return;
    
    const userMsg = message;
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setMessage('');
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/chat/investment`, 
        { message: userMsg, session_id: sessionId }
      );
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.ai_response }]);
    } catch (error) {
      toast.error('Error al enviar mensaje');
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    if (window.confirm('¿Borrar todo el historial de conversación?')) {
      setMessages([]);
      if (sessionId) {
        localStorage.removeItem(`invest_messages_${sessionId}`);
      }
      toast.success('Historial borrado');
    }
  };

  if (!['profesional', 'premium', 'usuario_avanzado', 'essential'].includes(user?.subscription_plan)) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-lg text-center">
          <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${currentTheme.gradient} flex items-center justify-center mx-auto mb-6`}>
            <Crown size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Chat IA Avanzado</h1>
          <p className="text-gray-400 mb-6 text-sm">
            El chat con nuestro asesor IA experto en inmobiliario esta disponible desde el plan Avanzado.
          </p>
          <button
            onClick={() => navigate('/select-plan')}
            data-testid="upgrade-to-premium-btn"
            className={`px-8 py-3 rounded-xl bg-gradient-to-r ${currentTheme.gradient} text-white font-medium hover:opacity-90 transition-all`}
          >
            Ver Planes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-950 flex flex-col">
      <div className="px-6 pt-20 pb-3 border-b border-white/10 flex justify-between items-center">
        <div>
          <PageTitle className="mb-1">Asesor IA Inmobiliario 24/7</PageTitle>
          <p className="text-xs text-gray-400">Pregunta sobre compra, venta o inversión • Historial guardado</p>
        </div>
        <button
          onClick={clearHistory}
          className="text-xs text-gray-500 hover:text-red-500 transition px-3 py-1 border border-gray-700 rounded"
        >
          Borrar historial
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center mt-16">
            <Bot size={48} className="mx-auto text-gray-700 mb-4" />
            <p className="text-gray-500 text-sm">Escribe tu primera pregunta para comenzar</p>
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {['Que ROI puedo esperar en Madrid?', 'Mejor zona para invertir en Barcelona?', 'Es buen momento para comprar?'].map((q, i) => (
                <button key={i} onClick={() => { setMessage(q); }} className="text-xs bg-gray-800 border border-white/10 text-gray-300 px-3 py-2 rounded-lg hover:bg-gray-700 transition-all">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] ${
              msg.role === 'user'
                ? `bg-gradient-to-r ${currentTheme.gradient} text-white`
                : 'bg-gray-800 text-gray-200'
            } rounded-2xl px-4 py-3`}>
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-800 rounded-2xl px-4 py-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="p-4 border-t border-white/10">
        <div className="flex gap-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            className="flex-1 bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
            placeholder="Pregunta sobre inversiones inmobiliarias..."
            disabled={loading}
            data-testid="chat-input"
          />
          <button
            onClick={sendMessage}
            disabled={loading || !message.trim()}
            className={`p-3 rounded-xl bg-gradient-to-r ${currentTheme.gradient} text-white hover:opacity-90 disabled:opacity-50 transition-all`}
            data-testid="send-message-btn"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};
